import { useCallback, useEffect } from 'react';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import { Box, Divider, GlobalStyles, IconButton, Tooltip } from '@mui/material';
import Document from '@tiptap/extension-document'
import FileHandler from '@tiptap/extension-file-handler'
import Image from '@tiptap/extension-image'
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import { type Editor, EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { FirmAssetSignedUrlResponse, useFirmAssets } from 'services/hooks';
import { STANDALONE_COMPANIES_FIRM_ID } from '../constants';

const TOOLBAR_BUTTON_SX = {
  padding: '4px',
  borderRadius: '4px',
  '&:hover': { backgroundColor: 'action.hover' },
} as const;

const ACTIVE_BUTTON_SX = {
  ...TOOLBAR_BUTTON_SX,
  backgroundColor: 'action.selected',
  '&:hover': { backgroundColor: 'action.selected' },
} as const;

const TOOLBAR_ICON_SX = { fontSize: '1.1rem' } as const;

interface RichTextEditorProps {
  content: string;
  onChange: (html: string) => void;
  isDisabled?: boolean;
  placeholder?: string;
  autoFocus?: boolean;
}

const RichTextNoteEditor = ({
  content,
  onChange,
  isDisabled = false,
  placeholder = 'Type your note here...',
  autoFocus = false,
}: RichTextEditorProps) => {
  const firmId = STANDALONE_COMPANIES_FIRM_ID;
  const { getFirmAssetSignedUrl, uploadImageFile } = useFirmAssets();

  const uploadAndInsertImage = useCallback(
    async (currentEditor: Editor, file: File, position: number) => {
      if (!firmId) {
        return;
      }

      const fileExtension = file.type.split('/')[1] ?? 'png';
      const signedUrl = (await getFirmAssetSignedUrl(firmId, fileExtension)) as FirmAssetSignedUrlResponse;

      if (!signedUrl?.url) {
        return;
      }

      const uploadResponse = (await uploadImageFile(signedUrl.url, file)) as Response;

      if (!uploadResponse?.ok) {
        return;
      }

      currentEditor
        .chain()
        .insertContentAt(position, {
          type: 'image',
          attrs: {
            src: signedUrl.url.split('?')[0],
          },
        })
        .focus()
        .run();
    },
    [firmId, getFirmAssetSignedUrl, uploadImageFile]
  );

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Placeholder.configure({ placeholder }),
      Document,
      FileHandler.configure({
        allowedMimeTypes: ['image/png', 'image/jpeg', 'image/gif', 'image/webp'],
        onDrop: (currentEditor, files, pos) => {
          Promise.all(files.map(file => uploadAndInsertImage(currentEditor, file, pos))).catch(() => undefined);
        },
        onPaste: (currentEditor, files, htmlContent) => {
          if (htmlContent) {
            return false;
          }

          const insertPosition = currentEditor.state.selection.anchor;
          Promise.all(files.map(file => uploadAndInsertImage(currentEditor, file, insertPosition))).catch(() => undefined);
          return true;
        },
      }),
      Image.configure({
        inline: true,
        allowBase64: true,
        resize: {
          enabled: true,
          alwaysPreserveAspectRatio: true,
          minWidth: 50,
          minHeight: 50,
        }
      })
    ],
    content,
    editable: !isDisabled,
    autofocus: isDisabled ? false : 'end',
    onUpdate: ({ editor: e }) => {
      onChange(e.getHTML());
    },
  });

  // Sync external content changes (e.g. switching between notes)
  useEffect(() => {
    if (editor && editor.getHTML() !== content) {
      editor.commands.setContent(content, { emitUpdate: false });
    }
  }, [content, editor]);

  // Sync editable state
  useEffect(() => {
    if (editor) {
      editor.setEditable(!isDisabled);
    }
  }, [isDisabled, editor]);

  useEffect(() => {
    if (!autoFocus || !editor) {
      return;
    }
    const id = requestAnimationFrame(() => {
      editor.commands.focus('end');
    });
    // eslint-disable-next-line consistent-return
    return () => cancelAnimationFrame(id); // Cleanup function to cancel the animation frame
  }, [autoFocus, editor]);

  const execFormat = useCallback(
    (command: () => void) => {
      if (isDisabled || !editor) {
        return;
      }
      command();
    },
    [isDisabled, editor]
  );

  return (
    <>
      <GlobalStyles
        styles={{
          '.ProseMirror p.is-editor-empty:first-of-type::before': {
            content: 'attr(data-placeholder)',
            float: 'left',
            color: 'rgba(0, 0, 0, 0.38)',
            pointerEvents: 'none',
            height: 0,
          },
          '.ProseMirror': { outline: 'none' },
          '[data-resize-wrapper]': {
            '& img': {
              maxWidth: '350px',
              maxHeight: '350px',
              minWidth: '100px',
              minHeight: '100px',
            },
          },
          '[data-resize-handle]': {
            width: '10px',
            height: '10px',
            backgroundColor: 'black',
            position: 'absolute',
            zIndex: 1000,
            transform: 'translateY(-2px)',
          },
          '[data-resize-handle="top-left"], [data-resize-handle][data-position="top-left"], [data-resize-handle][data-handle-position="top-left"]': {
            clipPath: 'polygon(0% 0%, 100% 0%, 0% 100%)',
            cursor: 'nwse-resize',
          },
          '[data-resize-handle="top-right"], [data-resize-handle][data-position="top-right"], [data-resize-handle][data-handle-position="top-right"]': {
            clipPath: 'polygon(0% 0%, 100% 0%, 100% 100%)',
            cursor: 'nesw-resize',
          },
          '[data-resize-handle="bottom-right"], [data-resize-handle][data-position="bottom-right"], [data-resize-handle][data-handle-position="bottom-right"]': {
            clipPath: 'polygon(100% 0%, 100% 100%, 0% 100%)',
            cursor: 'nwse-resize',
          },
          '[data-resize-handle="bottom-left"], [data-resize-handle][data-position="bottom-left"], [data-resize-handle][data-handle-position="bottom-left"]': {
            clipPath: 'polygon(0% 0%, 100% 100%, 0% 100%)',
            cursor: 'nesw-resize',
          },
        }}
      />
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Toolbar */}
        {!isDisabled && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '2px',
              px: 0.5,
              py: 0.5,
              borderBottom: '1px solid',
              borderColor: 'divider',
              backgroundColor: 'grey.50',
              flexShrink: 0,
            }}
          >
            <Tooltip title="Bold (Ctrl+B)">
              <IconButton
                size="small"
                onMouseDown={e => e.preventDefault()}
                onClick={() => execFormat(() => editor?.chain().focus().toggleBold().run())}
                sx={editor?.isActive('bold') ? ACTIVE_BUTTON_SX : TOOLBAR_BUTTON_SX}
                aria-label="Bold"
              >
                <FormatBoldIcon sx={TOOLBAR_ICON_SX} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Italic (Ctrl+I)">
              <IconButton
                size="small"
                onMouseDown={e => e.preventDefault()}
                onClick={() => execFormat(() => editor?.chain().focus().toggleItalic().run())}
                sx={editor?.isActive('italic') ? ACTIVE_BUTTON_SX : TOOLBAR_BUTTON_SX}
                aria-label="Italic"
              >
                <FormatItalicIcon sx={TOOLBAR_ICON_SX} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Underline (Ctrl+U)">
              <IconButton
                size="small"
                onMouseDown={e => e.preventDefault()}
                onClick={() => execFormat(() => editor?.chain().focus().toggleUnderline().run())}
                sx={editor?.isActive('underline') ? ACTIVE_BUTTON_SX : TOOLBAR_BUTTON_SX}
                aria-label="Underline"
              >
                <FormatUnderlinedIcon sx={TOOLBAR_ICON_SX} />
              </IconButton>
            </Tooltip>
            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
            <Tooltip title="Bullet List">
              <IconButton
                size="small"
                onMouseDown={e => e.preventDefault()}
                onClick={() => execFormat(() => editor?.chain().focus().toggleBulletList().run())}
                sx={editor?.isActive('bulletList') ? ACTIVE_BUTTON_SX : TOOLBAR_BUTTON_SX}
                aria-label="Bullet List"
              >
                <FormatListBulletedIcon sx={TOOLBAR_ICON_SX} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Numbered List">
              <IconButton
                size="small"
                onMouseDown={e => e.preventDefault()}
                onClick={() => execFormat(() => editor?.chain().focus().toggleOrderedList().run())}
                sx={editor?.isActive('orderedList') ? ACTIVE_BUTTON_SX : TOOLBAR_BUTTON_SX}
                aria-label="Numbered List"
              >
                <FormatListNumberedIcon sx={TOOLBAR_ICON_SX} />
              </IconButton>
            </Tooltip>
          </Box>
        )}

        {/* Editor Area */}
        <Box
          sx={{
            flex: 1,
            p: 1,
            overflowY: 'auto',
            fontSize: '0.875rem',
            lineHeight: 1.6,
            wordBreak: 'break-word',
            '& .ProseMirror': {
              height: '100%',
              outline: 'none',
            },
            '& ul, & ol': {
              paddingLeft: '1.5rem',
              margin: '0.25rem 0',
            },
            '& li': {
              marginBottom: '0.125rem',
            },
          }}
        >
          <EditorContent editor={editor} />
        </Box>
      </Box>
    </>
  );
};

export default RichTextNoteEditor;
